import { arrayUnion, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { app, firestore } from '../views/firebaseConfig';
import { COLLECTIONS } from '../contracts/p4r';

const PREF_KEY = 'p4r_web_notification_prefs_v1';
export const DEFAULT_NOTIFICATION_PREFS = {
  setupSeen: false,
  messagesEnabled: true,
  tournamentsEnabled: true,
  teamsEnabled: true,
  nearbyEnabled: true,
};

function workerUrl() {
  const params = new URLSearchParams({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  });
  return `/firebase-messaging-sw.js?${params.toString()}`;
}

export function getWebNotificationPrefs() {
  try {
    return { ...DEFAULT_NOTIFICATION_PREFS, ...JSON.parse(localStorage.getItem(PREF_KEY) || '{}') };
  } catch (_) {
    return { ...DEFAULT_NOTIFICATION_PREFS };
  }
}

export async function saveWebNotificationPrefs(uid, next) {
  const prefs = { ...getWebNotificationPrefs(), ...next, setupSeen: true };
  localStorage.setItem(PREF_KEY, JSON.stringify(prefs));
  if (uid) {
    await setDoc(doc(firestore, COLLECTIONS.USER_PROFILES, uid), {
      webNotificationPreferences: {
        messagesEnabled: !!prefs.messagesEnabled,
        tournamentsEnabled: !!prefs.tournamentsEnabled,
        teamsEnabled: !!prefs.teamsEnabled,
        nearbyEnabled: !!prefs.nearbyEnabled,
      },
      webNotificationSetupSeen: true,
      webNotificationUpdatedAt: serverTimestamp(),
    }, { merge: true });
  }
  return prefs;
}

export function webNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return window.Notification.permission;
}

export async function enableWebNotifications(uid) {
  if (!uid) throw new Error('Sign in before enabling notifications.');
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    throw new Error('This browser does not support web notifications.');
  }
  if (!(await isSupported())) throw new Error('Firebase messaging is not supported in this browser.');

  const permission = await window.Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was not granted.');
  const vapidKey = process.env.REACT_APP_FIREBASE_WEB_PUSH_VAPID_KEY;
  if (!vapidKey) throw new Error('Web push is not configured. Add REACT_APP_FIREBASE_WEB_PUSH_VAPID_KEY.');

  const registration = await navigator.serviceWorker.register(workerUrl());
  const token = await getToken(getMessaging(app), { vapidKey, serviceWorkerRegistration: registration });
  if (!token) throw new Error('The browser did not return a notification token.');

  await setDoc(doc(firestore, COLLECTIONS.USER_PROFILES, uid), {
    webFcmToken: token,
    webFcmTokens: arrayUnion(token),
    webFcmPlatform: 'web',
    webNotificationPermission: 'granted',
    webNotificationUpdatedAt: serverTimestamp(),
  }, { merge: true });
  return token;
}

export async function syncWebNotificationToken(uid) {
  if (!uid || webNotificationPermission() !== 'granted') return null;
  try { return await enableWebNotifications(uid); } catch (_) { return null; }
}

export function notificationRouteFromData(data = {}) {
  if (data.path?.startsWith('/')) return data.path;
  if (data.teamId || data.team_id) return `/bucks/teams/${data.teamId || data.team_id}`;
  if (data.tournamentId || data.tournament_id) return `/bucks/tournaments/${data.tournamentId || data.tournament_id}`;
  if (data.conversationId || data.conversation_id || data.open_messages === 'true') return '/messages';
  if (data.forsaleId || data.forsale_id) return '/shoparound';
  if (data.open_bucks === 'true') return '/bucks';
  return '/messages';
}

export async function listenForForegroundNotifications(onNotification) {
  if (!(await isSupported())) return () => {};
  return onMessage(getMessaging(app), (payload) => onNotification?.(payload));
}
